import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface PollingOptions {
  applicationId: string;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

interface PollingState {
  isPolling: boolean;
  currentStep: number;
  error: string | null;
}

/**
 * Hook for polling Caprion application status using Celery task pattern.
 * 
 * Pattern:
 * 1. Call start-status-poll to start Celery task (returns task_id)
 * 2. Poll task status (not Caprion API) until task completes
 * 3. Only 1 Caprion API call happens (in the Celery task)
 * 
 * Similar to get-agreement-pdf pattern.
 */
export function useCaprionStatusPolling() {
  const [state, setState] = useState<PollingState>({
    isPolling: false,
    currentStep: 0,
    error: null,
  });

  const pollingIntervalRef = useRef<number | null>(null);
  const isResolvedRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setState(prev => ({ ...prev, isPolling: false }));
  }, []);

  const startPolling = useCallback(async (options: PollingOptions): Promise<boolean> => {
    const { applicationId, onSuccess, onError } = options;

    // Reset state
    isResolvedRef.current = false;
    setState({
      isPolling: true,
      currentStep: 0,
      error: null,
    });

    try {
      // Step 1: Start Celery task
      console.log('[Caprion Polling] Starting Celery task for application:', applicationId);
      
      const startResponse = await axios.post(
        `${API_BASE_URL}/api/caprion/start-status-poll/`,
        { application_id: applicationId },
        { withCredentials: true }
      );

      if (!startResponse.data.success) {
        throw new Error(startResponse.data.error || 'Failed to start status polling');
      }

      const taskId = startResponse.data.task_id;
      console.log('[Caprion Polling] Celery task started with task_id:', taskId);

      // Step 2: Poll task status (not Caprion API)
      return new Promise((resolve) => {
        let pollCount = 0;
        const maxPolls = 60; // 60 polls * 2 seconds = 2 minutes max

        const poll = async () => {
          if (isResolvedRef.current) {
            stopPolling();
            return;
          }

          pollCount++;
          console.log(`[Caprion Polling] Poll attempt ${pollCount}/${maxPolls}`);

          // Update step indicator (cycle through 3 steps)
          setState(prev => ({
            ...prev,
            currentStep: (pollCount % 3),
          }));

          try {
            const response = await axios.get(
              `${API_BASE_URL}/api/caprion/poll-status/${taskId}/`,
              { withCredentials: true }
            );

            console.log('[Caprion Polling] Task state:', response.data.state);

            if (response.data.is_ready && response.data.state === 'SUCCESS') {
              // Task completed successfully
              console.log('[Caprion Polling] ✅ Task completed successfully');
              isResolvedRef.current = true;
              stopPolling();
              onSuccess?.(response.data.result);
              resolve(true);
            } else if (response.data.state === 'FAILURE') {
              // Task failed
              console.error('[Caprion Polling] ❌ Task failed:', response.data.error);
              isResolvedRef.current = true;
              stopPolling();
              const errorMsg = response.data.error || 'Status check failed';
              setState(prev => ({ ...prev, error: errorMsg }));
              onError?.(errorMsg);
              resolve(false);
            } else if (pollCount >= maxPolls) {
              // Timeout
              console.error('[Caprion Polling] ⏱️ Polling timeout');
              isResolvedRef.current = true;
              stopPolling();
              const errorMsg = 'Status check timed out. Please try again.';
              setState(prev => ({ ...prev, error: errorMsg }));
              onError?.(errorMsg);
              resolve(false);
            }
            // Otherwise, continue polling (PENDING, RETRY, STARTED, etc.)
          } catch (error: any) {
            console.error('[Caprion Polling] Poll error:', error);
            isResolvedRef.current = true;
            stopPolling();
            const errorMsg = error.response?.data?.error || error.message || 'Failed to check status';
            setState(prev => ({ ...prev, error: errorMsg }));
            onError?.(errorMsg);
            resolve(false);
          }
        };

        // Start polling immediately, then every 2 seconds
        poll();
        pollingIntervalRef.current = setInterval(poll, 2000);
      });
    } catch (error: any) {
      console.error('[Caprion Polling] Start task error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to start status polling';
      setState({
        isPolling: false,
        currentStep: 0,
        error: errorMsg,
      });
      onError?.(errorMsg);
      return false;
    }
  }, [stopPolling]);

  return {
    ...state,
    startPolling,
    stopPolling,
  };
}
